import { useState, useEffect } from 'react';
import {
  Upload, RefreshCw, ImageIcon, Save, X, Edit, Star,
  Package, Shield, Home, Database, AlertTriangle, Settings,
  Cloud, HardDrive, BarChart3, Zap
} from 'lucide-react';
import QuickDbSetup from '../components/QuickDbSetup';
import AdminSettings from '../components/AdminSettings';
import firebaseMetadataService from '../services/firebaseMetadataService';
import hybridImageService from '../services/hybridImageService';
import vercelBlobService from '../services/vercelBlobService';
import VercelBlobUploader from '../components/VercelBlobUploader';

const HybridSuperAdmin = () => {
  // State management
  const [activeTab, setActiveTab] = useState('products');
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testingFirebase, setTestingFirebase] = useState(false);
  const [storageStats, setStorageStats] = useState(null);
  const [showVercelUploader, setShowVercelUploader] = useState(false);

  // Load images from hybrid storage on component mount
  useEffect(() => {
    loadImages();
    loadStorageStats();

    // Set up global refresh function for other components to call
    window.refreshAllImages = loadImages;

    // Cleanup on unmount
    return () => {
      delete window.refreshAllImages;
    };
  }, []);

  const loadImages = async () => {
    try {
      setLoadingImages(true);
      console.log('🔄 Loading ALL WEBSITE images from hybrid storage...');

      // Load images from both Supabase and Vercel Blob
      const allImages = await hybridImageService.getAllImages();
      
      setImages(allImages);
      console.log(`✅ Loaded ${allImages.length} images from hybrid storage`);

      // Count by storage provider
      const supabaseCount = allImages.filter(img => img.storage === 'supabase').length;
      const vercelBlobCount = allImages.filter(img => img.storage === 'vercel-blob').length;
      console.log(`📊 Supabase: ${supabaseCount}, Vercel Blob: ${vercelBlobCount}`);
      
    } catch (error) {
      console.error('❌ Error loading hybrid images:', error);
    } finally {
      setLoadingImages(false);
    }
  };

  const loadStorageStats = async () => {
    try {
      const stats = await hybridImageService.getStorageStats();
      setStorageStats(stats);
    } catch (error) {
      console.error('Error loading storage stats:', error);
    }
  };

  const testFirebaseConnection = async () => {
    try {
      setTestingFirebase(true);
      console.log('🔄 Testing Firebase connection...');

      await firebaseMetadataService.testConnection();
      alert('✅ Firebase connection test successful! You can save metadata.');
    } catch (error) {
      console.error('❌ Firebase test failed:', error);
      alert(`❌ Firebase connection failed: ${error.message}`);
    } finally {
      setTestingFirebase(false);
    }
  };

  const handleImageUpload = async (file, storageProvider = 'supabase') => {
    try {
      setUploading(true);
      console.log(`📤 Uploading image to ${storageProvider}...`);

      if (storageProvider === 'vercel-blob') {
        // Upload to Vercel Blob
        const result = await vercelBlobService.uploadProductImage(file, file.name);
        if (result.success) {
          console.log('✅ Image uploaded successfully to Vercel Blob');
        } else {
          throw new Error(result.error);
        }
      } else {
        // Upload to Supabase (legacy)
        const { default: supabaseService } = await import('../services/supabase');
        const fileName = `${Date.now()}-${file.name}`;
        const filePath = `products/${fileName}`;
        await supabaseService.uploadImage(file, filePath);
        console.log('✅ Image uploaded successfully to Supabase');
      }

      // Trigger refresh across all pages
      await loadImages();
      await loadStorageStats();

      // Trigger refresh on other pages
      if (window.refreshHomeImages) {
        window.refreshHomeImages();
      }
      if (window.refreshGalleryImages) {
        window.refreshGalleryImages();
      }

      // Dispatch global refresh event
      window.dispatchEvent(new CustomEvent('refreshAllImages'));
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      alert(`Failed to upload image: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (image) => {
    if (!confirm(`Are you sure you want to delete "${image.title || image.name}"? This will remove it from ALL sections of the website.`)) {
      return;
    }

    try {
      console.log(`🗑️ Deleting image from ${image.storage}...`);

      if (image.storage === 'vercel-blob') {
        await vercelBlobService.deleteFile(image.url);
      } else {
        const { default: supabaseService } = await import('../services/supabase');
        await supabaseService.deleteImage(image.fullPath);
      }

      console.log('✅ Image deleted successfully');

      // Trigger refresh across all pages
      await loadImages();
      await loadStorageStats();

      // Trigger refresh on other pages
      if (window.refreshHomeImages) {
        window.refreshHomeImages();
      }
      if (window.refreshGalleryImages) {
        window.refreshGalleryImages();
      }

      // Dispatch global refresh event
      window.dispatchEvent(new CustomEvent('refreshAllImages'));
    } catch (error) {
      console.error('❌ Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-red-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Hybrid Super Admin</h1>
                <p className="text-sm text-gray-600">Manage images across Supabase & Vercel Blob storage</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.open('/', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
              >
                <Home className="w-4 h-4 mr-2" />
                View Website
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('products')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'products'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Package className="w-4 h-4 inline mr-2" />
              All Images (Hybrid)
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'upload'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              Upload Images
            </button>
            <button
              onClick={() => setActiveTab('storage')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'storage'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Storage Analytics
            </button>
            <button
              onClick={() => setActiveTab('database')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'database'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              Database Setup
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Site Settings
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ImageIcon className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Total Images</p>
                    <p className="text-lg font-bold text-gray-900">{images.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <Cloud className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Vercel Blob</p>
                    <p className="text-lg font-bold text-gray-900">
                      {images.filter(img => img.storage === 'vercel-blob').length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <HardDrive className="w-4 h-4 text-orange-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Supabase</p>
                    <p className="text-lg font-bold text-gray-900">
                      {images.filter(img => img.storage === 'supabase').length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Star className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Featured</p>
                    <p className="text-lg font-bold text-gray-900">{images.filter(img => img.featured).length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Package className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">In Stock</p>
                    <p className="text-lg font-bold text-gray-900">{images.filter(img => img.inStock).length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Hybrid Storage Management</h3>
                  <p className="text-sm text-gray-600">Manage images from both Supabase and Vercel Blob storage providers</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={testFirebaseConnection}
                    disabled={testingFirebase}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center disabled:opacity-50"
                  >
                    <Database className={`w-4 h-4 mr-2 ${testingFirebase ? 'animate-spin' : ''}`} />
                    Test Firebase
                  </button>
                  <button
                    onClick={() => {
                      loadImages();
                      loadStorageStats();
                    }}
                    disabled={loadingImages}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingImages ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>
            
            {loadingImages ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                <p className="mt-2 text-gray-600">Loading hybrid storage images...</p>
              </div>
            ) : images.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-12 h-12 mx-auto text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No images found</h3>
                <p className="mt-1 text-sm text-gray-500">Upload images to either Supabase or Vercel Blob storage.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {images.map((image) => (
                  <HybridProductCard
                    key={`${image.storage}-${image.id}`}
                    image={image}
                    onDelete={() => handleDeleteImage(image)}
                    onRefresh={loadImages}
                  />
                ))}
              </div>
            )}
            </div>
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            {/* Upload Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Vercel Blob Upload */}
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Cloud className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900">Vercel Blob Upload</h3>
                    <p className="text-sm text-green-700">Recommended • Global CDN • Auto-optimization</p>
                  </div>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-700">
                    <Zap className="w-4 h-4 mr-2 text-green-600" />
                    Global CDN with 100+ edge locations
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <ImageIcon className="w-4 h-4 mr-2 text-green-600" />
                    Automatic image optimization
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Shield className="w-4 h-4 mr-2 text-green-600" />
                    Enterprise security & 99.9% uptime
                  </div>
                </div>

                <button
                  onClick={() => setShowVercelUploader(true)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg flex items-center justify-center"
                >
                  <Cloud className="w-4 h-4 mr-2" />
                  Upload to Vercel Blob
                </button>
              </div>

              {/* Supabase Upload */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <HardDrive className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900">Supabase Upload</h3>
                    <p className="text-sm text-blue-700">Legacy • Stable • Database-connected</p>
                  </div>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-700">
                    <Database className="w-4 h-4 mr-2 text-blue-600" />
                    PostgreSQL-based storage
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Shield className="w-4 h-4 mr-2 text-blue-600" />
                    Reliable and stable
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Package className="w-4 h-4 mr-2 text-blue-600" />
                    Direct database integration
                  </div>
                </div>

                <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center">
                  <label className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg cursor-pointer inline-flex items-center">
                    <HardDrive className="w-4 h-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Upload to Supabase'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        files.forEach(file => handleImageUpload(file, 'supabase'));
                      }}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>

                  {uploading && (
                    <div className="mt-4">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                      <p className="text-sm text-gray-600 mt-2">Uploading to Supabase...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Storage Analytics Tab */}
        {activeTab === 'storage' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Storage Analytics</h3>
              
              {storageStats ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <Cloud className="w-8 h-8 text-green-600 mr-3" />
                      <div>
                        <h4 className="text-lg font-semibold text-green-900">Vercel Blob</h4>
                        <p className="text-sm text-green-700">Modern cloud storage</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-green-800">Images:</span>
                        <span className="font-medium text-green-900">{storageStats.vercelBlob?.count || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-green-800">Total Size:</span>
                        <span className="font-medium text-green-900">
                          {formatBytes(storageStats.vercelBlob?.totalSize || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-green-800">Status:</span>
                        <span className="font-medium text-green-900">Primary</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <HardDrive className="w-8 h-8 text-blue-600 mr-3" />
                      <div>
                        <h4 className="text-lg font-semibold text-blue-900">Supabase</h4>
                        <p className="text-sm text-blue-700">Legacy database storage</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-blue-800">Images:</span>
                        <span className="font-medium text-blue-900">{storageStats.supabase?.count || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-blue-800">Total Size:</span>
                        <span className="font-medium text-blue-900">
                          {formatBytes(storageStats.supabase?.totalSize || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-blue-800">Status:</span>
                        <span className="font-medium text-blue-900">Fallback</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                  <p className="mt-2 text-gray-600">Loading storage analytics...</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Database Setup Tab */}
        {activeTab === 'database' && (
          <QuickDbSetup />
        )}

        {/* Site Settings Tab */}
        {activeTab === 'settings' && (
          <AdminSettings />
        )}
      </div>

      {/* Vercel Blob Uploader Modal */}
      {showVercelUploader && (
        <VercelBlobUploader
          onClose={() => setShowVercelUploader(false)}
          onUploadComplete={(results) => {
            console.log('Vercel Blob upload completed:', results);
            setShowVercelUploader(false);
            loadImages();
            loadStorageStats();
          }}
        />
      )}
    </div>
  );
};

// Helper function to format bytes
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Hybrid Product Card Component
const HybridProductCard = ({ image, onDelete, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    title: image.title || '',
    description: image.description || '',
    price: image.price || '',
    category: image.category || 'Components',
    inStock: image.inStock !== undefined ? image.inStock : true,
    featured: image.featured || false
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('💾 Saving hybrid product data to Firebase...');
      console.log('📝 Image name:', image.name);
      console.log('📝 Storage:', image.storage);
      console.log('📝 Edit data:', editData);

      // Validate required fields
      if (!editData.title.trim()) {
        alert('❌ Title is required');
        return;
      }

      // Save to Firebase with all metadata
      const metadataToSave = {
        title: editData.title.trim(),
        description: editData.description.trim(),
        category: editData.category.trim(),
        price: editData.price ? parseFloat(editData.price) : null,
        currency: 'K',
        inStock: editData.inStock,
        featured: editData.featured,
        storage_provider: image.storage,
        public_url: image.url || image.publicUrl,
        file_path: image.fullPath || image.pathname,
        // Add both formats for compatibility
        publicUrl: image.url || image.publicUrl,
        fullPath: image.fullPath || image.pathname,
        // Vercel Blob specific fields
        ...(image.storage === 'vercel-blob' && {
          vercel_blob_url: image.url,
          vercel_blob_pathname: image.pathname,
          vercel_blob_download_url: image.downloadUrl
        })
      };

      console.log('📤 Sending to Firebase:', metadataToSave);

      const result = await firebaseMetadataService.saveImageMetadata(image.name, metadataToSave);

      console.log('✅ Firebase save result:', result);
      console.log('✅ Hybrid product data saved successfully to Firebase');

      // Show success message
      alert('✅ Product data saved successfully!');

      // Trigger refresh across all pages
      if (onRefresh) {
        await onRefresh();
      }

      // Trigger refresh on other pages
      if (window.refreshHomeImages) {
        window.refreshHomeImages();
      }
      if (window.refreshGalleryImages) {
        window.refreshGalleryImages();
      }

      // Dispatch global refresh event
      window.dispatchEvent(new CustomEvent('refreshAllImages'));

      setIsEditing(false);
    } catch (error) {
      console.error('❌ Error saving hybrid product data:', error);
      alert(`❌ Failed to save changes: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getStorageIcon = () => {
    if (image.storage === 'vercel-blob') {
      return <Cloud className="w-4 h-4 text-green-600" />;
    }
    return <HardDrive className="w-4 h-4 text-blue-600" />;
  };

  const getStorageBadge = () => {
    if (image.storage === 'vercel-blob') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Cloud className="w-3 h-3 mr-1" />
          Vercel Blob
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <HardDrive className="w-3 h-3 mr-1" />
        Supabase
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Image */}
      <div className="aspect-square bg-gray-100 relative">
        <img
          src={image.url || image.publicUrl}
          alt={image.title || image.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = '/api/placeholder/200/200';
          }}
        />

        {/* Storage Badge */}
        <div className="absolute top-2 left-2">
          {getStorageBadge()}
        </div>

        {/* Featured Badge */}
        {image.featured && (
          <div className="absolute top-2 right-2">
            <Star className="w-5 h-5 text-yellow-500 fill-current" />
          </div>
        )}

        {/* Actions */}
        <div className="absolute bottom-2 right-2 flex space-x-1">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="bg-white bg-opacity-90 hover:bg-opacity-100 p-1.5 rounded-full shadow-sm"
            title="Edit"
          >
            <Edit className="w-3 h-3 text-gray-600" />
          </button>
          <button
            onClick={onDelete}
            className="bg-white bg-opacity-90 hover:bg-opacity-100 p-1.5 rounded-full shadow-sm"
            title="Delete"
          >
            <X className="w-3 h-3 text-red-600" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {isEditing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editData.title}
              onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Product title"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            />
            <textarea
              value={editData.description}
              onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description"
              rows="2"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                value={editData.price}
                onChange={(e) => setEditData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="Price (K)"
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={editData.category}
                onChange={(e) => setEditData(prev => ({ ...prev, category: e.target.value }))}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Components">Components</option>
                <option value="Arduino & Development Boards">Arduino & Development Boards</option>
                <option value="Sensors & Modules">Sensors & Modules</option>
                <option value="Display & Interface Modules">Display & Interface Modules</option>
                <option value="Motors & Motor Drivers">Motors & Motor Drivers</option>
                <option value="Other Components">Other Components</option>
              </select>
            </div>
            <div className="flex items-center space-x-4 text-sm">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editData.inStock}
                  onChange={(e) => setEditData(prev => ({ ...prev, inStock: e.target.checked }))}
                  className="mr-1"
                />
                In Stock
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editData.featured}
                  onChange={(e) => setEditData(prev => ({ ...prev, featured: e.target.checked }))}
                  className="mr-1"
                />
                Featured
              </label>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50"
              >
                {saving ? <RefreshCw className="w-3 h-3 animate-spin mx-auto" /> : 'Save'}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-1.5 rounded text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h4 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
              {image.title || image.name}
            </h4>
            {image.description && (
              <p className="text-xs text-gray-600 mb-2 line-clamp-2">{image.description}</p>
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {image.price && (
                  <span className="text-sm font-medium text-green-600">K{image.price}</span>
                )}
                {image.inStock ? (
                  <span className="text-xs text-green-600">In Stock</span>
                ) : (
                  <span className="text-xs text-red-600">Out of Stock</span>
                )}
              </div>
              {getStorageIcon()}
            </div>
            {image.category && (
              <p className="text-xs text-gray-500 mt-1">{image.category}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HybridSuperAdmin;
